import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { APP_ROUTE_DEFINITIONS } from "@/features/navigation";
import { useRuntimeState } from "@/features/runtime";
import { formatSafeAddress } from "@/features/home/formatSafeAddress";
import type { RuntimeState } from "@/safe/runtimePolicy";
import { useSafe } from "@/safe/SafeContextValue";
import { useWatchedSafe } from "@/safe/WatchedSafeContext";

type AppRouteDefinition = (typeof APP_ROUTE_DEFINITIONS)[number];

function describeWritePathBlocker(blocker: string, t: TFunction): string {
  if (blocker === "OUTSIDE_SAFE") return t("app.runtime.blocker.outsideSafe");
  if (blocker === "UNSUPPORTED_CHAIN") return t("app.runtime.blocker.unsupportedChain");
  if (blocker === "SAFE_NONCE_MISSING") return t("app.runtime.blocker.safeNonceMissing");
  if (blocker === "SAFE_NONCE_INVALID") return t("app.runtime.blocker.safeNonceInvalid");
  if (blocker === "SAFE_PARENT_ORIGIN_UNTRUSTED")
    return t("app.runtime.blocker.safeParentOriginUntrusted");
  if (blocker === "SAFE_POSTURE_UNKNOWN") return t("app.runtime.blocker.safePostureUnknown");
  return t("app.runtime.blocker.unknown", { blocker });
}

const runtimeOriginMismatchPattern = /^Runtime origin (.+) does not match canonical origin (.+)\.$/;
const MAIN_CONTENT_ID = "app-main-content";

function describeAuthenticityReason(reason: string, t: TFunction): string {
  if (reason === "Canonical origin metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.canonicalOriginInvalid");
  }
  if (reason === "Runtime origin metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.runtimeOriginInvalid");
  }
  if (reason === "Build commit metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.buildCommitInvalid");
  }
  if (reason === "Build release ID metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.buildReleaseInvalid");
  }
  if (reason === "Build timestamp metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.buildTimestampInvalid");
  }
  if (reason === "Source repository metadata is missing or invalid.") {
    return t("app.runtime.authenticityReason.sourceRepoInvalid");
  }

  const mismatchMatch = runtimeOriginMismatchPattern.exec(reason);
  if (mismatchMatch) {
    return t("app.runtime.authenticityReason.originMismatch", {
      currentOrigin: mismatchMatch[1],
      canonicalOrigin: mismatchMatch[2]
    });
  }

  return t("app.runtime.authenticityReason.unknown", { reason });
}

function NavItem({
  to,
  label,
  forceActive = false,
  onNavigate
}: {
  to: string;
  label: string;
  // Marks the item active for sibling routes it owns (for example Delegation
  // stays highlighted on /activity) beyond NavLink's own path matching.
  forceActive?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          "tw:inline-flex tw:min-h-11 tw:items-center tw:justify-center tw:rounded-lg tw:px-3 tw:py-2 tw:text-sm tw:font-semibold tw:no-underline tw:transition",
          isActive || forceActive
            ? "tw:bg-iron-800 tw:text-iron-50"
            : "tw:text-iron-200 tw:desktop-hover:bg-iron-900 tw:desktop-hover:text-iron-50"
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}

function normalizeNavPathname(pathname: string): string {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function SkipToMainContentLink({ label }: { label: string }) {
  const handleSkipToMainContent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(MAIN_CONTENT_ID)?.focus();
  };

  return (
    <a
      href={`#${MAIN_CONTENT_ID}`}
      onClick={handleSkipToMainContent}
      className="tw:sr-only tw:focus:not-sr-only tw:focus:fixed tw:focus:top-4 tw:focus:left-4 tw:focus:z-[60] tw:focus:rounded-md tw:focus:bg-iron-50 tw:focus:px-4 tw:focus:py-2 tw:focus:text-sm tw:focus:font-semibold tw:focus:text-iron-950 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400"
    >
      {label}
    </a>
  );
}

function LocaleNeutralValue({ children }: { children: React.ReactNode }) {
  return (
    <span data-locale-neutral="true" translate="no">
      {children}
    </span>
  );
}

function AppShellHeader({
  runtime,
  navRoutes
}: {
  runtime: RuntimeState;
  navRoutes: readonly AppRouteDefinition[];
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const safe = useSafe();
  const watchedSafe = useWatchedSafe();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const normalizedPathname = normalizeNavPathname(location.pathname || "/");
  // The active Safe is part of the shell: every page states WHICH Safe the
  // data belongs to, beside the mode pill that says what you can do with it.
  const connectedSafeAddress = safe.mode === "IN_SAFE" ? safe.safe?.safeAddress : undefined;
  const headerSafeAddress = connectedSafeAddress ?? watchedSafe.manualSelection?.address;
  const headerSafeLabel = connectedSafeAddress
    ? formatSafeAddress(connectedSafeAddress)
    : watchedSafe.manualSelection
      ? watchedSafe.manualSelection.source === "ens"
        ? watchedSafe.manualSelection.label
        : formatSafeAddress(watchedSafe.manualSelection.address)
      : undefined;
  // A routine startup handshake is not an incident: INITIALIZING renders a
  // neutral "Connecting…" pill, never the attention treatment. With a watched
  // Safe the read-only pill says "Watching" — the register in one word.
  const shellStatusLabel = runtime.capabilities.writePathEnabled
    ? t("app.runtime.status.ready")
    : runtime.mode === "INITIALIZING"
      ? t("app.runtime.status.connecting")
      : runtime.authenticity.writeBlocked
        ? t("app.runtime.status.needsAttention")
        : runtime.mode === "OUTSIDE_SAFE_READ_ONLY"
          ? headerSafeAddress
            ? t("app.runtime.status.watching")
            : t("app.runtime.status.viewOnly")
          : t("app.runtime.status.needsAttention");
  const shellStatusTone = runtime.capabilities.writePathEnabled
    ? "connected"
    : runtime.mode !== "INITIALIZING" && runtime.authenticity.writeBlocked
      ? "attention"
      : "neutral";
  // Attention is protective (writes stay locked), so it wears the sand caution
  // tone; red stays reserved for genuine failure or loss.
  const shellStatusClass =
    shellStatusTone === "connected"
      ? "tw:border-green/25 tw:bg-green/10 tw:text-green"
      : shellStatusTone === "attention"
        ? "tw:border-sand-700 tw:bg-sand-950/60 tw:text-sand-100"
        : "tw:border-iron-700 tw:bg-iron-900 tw:text-iron-100";
  const shellStatusDotClass =
    shellStatusTone === "connected"
      ? "tw:bg-green"
      : shellStatusTone === "attention"
        ? "tw:bg-sand-600"
        : "tw:bg-iron-500";

  return (
    <header className="tw:sticky tw:top-0 tw:z-50 tw:border-b tw:border-iron-800 tw:bg-iron-950/90 tw:backdrop-blur">
      {/* Below sm: hamburger layout. sm to lg: the nav takes its own row —
          deliberately, identically in every register — because brand + chips
          + nav provably cannot share the width without occlusion. From lg up
          the container fits both registers with margin, the row is nowrap,
          and only the chip cluster shrinks/truncates. */}
      <div className="tw:mx-auto tw:flex tw:max-w-5xl tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:px-4 tw:py-2 tw:sm:py-3 tw:lg:flex-nowrap">
        {/* From sm up this block flexes and compresses internally (chips
            truncate) so primary navigation never wraps to a second row when
            the Safe chip appears. */}
        <div className="tw:flex tw:min-w-0 tw:basis-full tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2 tw:sm:basis-auto tw:lg:shrink tw:lg:flex-nowrap tw:lg:overflow-hidden">
          <img
            src="/app-icon.svg"
            alt=""
            width={40}
            height={40}
            className="tw:h-10 tw:w-10 tw:rounded-xl tw:shadow-xs"
          />
          <div className="tw:min-w-0 tw:flex-1 tw:shrink-0 tw:basis-0 tw:leading-tight tw:sm:flex-none tw:sm:basis-auto">
            {/* The brand name never wraps; on desktop the chips give way
                instead (they can shrink, the wordmark cannot). */}
            <div className="tw:text-sm tw:font-bold tw:whitespace-nowrap tw:text-iron-50">
              {t("app.title")}
            </div>
          </div>
          <button
            type="button"
            aria-controls="app-mobile-navigation"
            aria-expanded={mobileNavOpen}
            aria-label={mobileNavOpen ? t("nav.mobile.close") : t("nav.mobile.open")}
            onClick={() => setMobileNavOpen((isOpen) => !isOpen)}
            className="tw:inline-flex tw:h-11 tw:w-11 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-iron-900 tw:text-iron-50 tw:shadow-xs tw:sm:hidden"
          >
            <svg aria-hidden="true" className="tw:h-5 tw:w-5" fill="none" viewBox="0 0 20 20">
              {mobileNavOpen ? (
                <path
                  d="m5 5 10 10M15 5 5 15"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.8"
                />
              ) : (
                <path
                  d="M4 6h12M4 10h12M4 14h12"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.8"
                />
              )}
            </svg>
          </button>
          <div className="tw:flex tw:w-full tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-2 tw:sm:w-auto tw:sm:min-w-0 tw:sm:shrink tw:lg:flex-nowrap">
            {/* The network chip yields once a Safe chip is present: the footer
                still states the network on every page, and three chips plus
                the wordmark and nav cannot share one honest row. */}
            {headerSafeAddress ? null : (
              <span
                className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-iron-700 tw:bg-iron-900 tw:px-2.5 tw:py-1 tw:text-[11px] tw:font-semibold tw:text-iron-200 tw:sm:px-3 tw:sm:py-1.5 tw:sm:text-xs"
                aria-label={t("app.runtime.footer.postureSummary")}
              >
                <span className="tw:h-1.5 tw:w-1.5 tw:rounded-full tw:bg-iron-300" />
                {t("app.runtime.footer.postureSummary")}
              </span>
            )}
            {headerSafeAddress && headerSafeLabel ? (
              <Link
                to="/"
                title={headerSafeAddress}
                aria-label={t("app.header.activeSafeAria", { label: headerSafeLabel })}
                className="tw:inline-flex tw:max-w-[11rem] tw:min-w-0 tw:shrink tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-iron-700 tw:bg-iron-900 tw:px-2.5 tw:py-1 tw:text-[11px] tw:font-semibold tw:text-iron-100 tw:no-underline tw:sm:px-3 tw:sm:py-1.5 tw:sm:text-xs tw:desktop-hover:bg-iron-800"
              >
                {connectedSafeAddress ? null : (
                  <svg
                    aria-hidden="true"
                    className="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-iron-400"
                    fill="none"
                    viewBox="0 0 16 16"
                  >
                    <path
                      d="M1.75 8S4 3.75 8 3.75 14.25 8 14.25 8 12 12.25 8 12.25 1.75 8 1.75 8Z"
                      stroke="currentColor"
                      strokeLinejoin="round"
                      strokeWidth="1.3"
                    />
                    <circle cx="8" cy="8" fill="currentColor" r="1.6" />
                  </svg>
                )}
                <span className="tw:truncate" data-locale-neutral="true" translate="no">
                  {headerSafeLabel}
                </span>
              </Link>
            ) : null}
            <span
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className={`tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:px-2.5 tw:py-1 tw:text-xs tw:font-semibold tw:sm:px-3 tw:sm:py-1.5 tw:sm:text-sm ${shellStatusClass}`}
            >
              <span className={`tw:h-1.5 tw:w-1.5 tw:rounded-full ${shellStatusDotClass}`} />
              {shellStatusLabel}
            </span>
          </div>
        </div>

        <nav className="tw:hidden tw:w-full tw:min-w-0 tw:flex-nowrap tw:items-center tw:gap-1 tw:sm:flex tw:sm:w-auto tw:sm:shrink-0">
          {navRoutes.map((route) => (
            <NavItem
              key={route.id}
              to={route.href}
              label={t(route.navLabelKey)}
              forceActive={route.alsoActiveHrefs?.includes(normalizedPathname) ?? false}
            />
          ))}
        </nav>

        {mobileNavOpen ? (
          <nav
            id="app-mobile-navigation"
            className="tw:grid tw:w-full tw:grid-cols-2 tw:gap-2 tw:sm:hidden"
          >
            {navRoutes.map((route) => (
              <NavItem
                key={route.id}
                to={route.href}
                label={t(route.navLabelKey)}
                forceActive={route.alsoActiveHrefs?.includes(normalizedPathname) ?? false}
                onNavigate={() => setMobileNavOpen(false)}
              />
            ))}
          </nav>
        ) : null}
      </div>
    </header>
  );
}

function WritePathAlert({ runtime }: { runtime: RuntimeState }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // While the handshake resolves, the runtime gate card already says writes
  // are locked; repeating it here would alarm every routine page load. The
  // wrong-network page likewise owns its own message — the shell staying
  // quiet there is what keeps the fact stated exactly once.
  if (
    runtime.mode === "INITIALIZING" ||
    runtime.capabilities.writePathEnabled ||
    (runtime.mode === "OUTSIDE_SAFE_READ_ONLY" && !runtime.authenticity.writeBlocked) ||
    (pathname.startsWith("/unsupported-network") && !runtime.authenticity.writeBlocked)
  ) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className="tw:mb-4 tw:rounded-xl tw:border tw:border-sand-700 tw:bg-sand-950/60 tw:p-4 tw:text-sm tw:text-sand-100"
    >
      <div className="tw:font-semibold">
        {runtime.authenticity.writeBlocked
          ? t("app.runtime.writeDisabled.authenticityBlocked")
          : t("app.runtime.writeDisabled.postureBlocked")}
      </div>
      {/* The authenticity bullet is filtered: it keys off the same predicate
          as the headline above it, so it could only ever restate it. The
          reasons list below carries the specifics. */}
      {runtime.writePath.blockers.filter((blocker) => blocker !== "AUTHENTICITY_UNVERIFIED")
        .length > 0 && (
        <ul className="tw:mt-2 tw:list-disc tw:space-y-1 tw:pl-5 tw:text-xs tw:text-sand-200">
          {runtime.writePath.blockers
            .filter((blocker) => blocker !== "AUTHENTICITY_UNVERIFIED")
            .map((blocker) => (
              <li key={blocker}>{describeWritePathBlocker(blocker, t)}</li>
            ))}
        </ul>
      )}
      {runtime.authenticity.reasons.length > 0 && (
        <ul className="tw:mt-2 tw:list-disc tw:space-y-1 tw:pl-5 tw:text-xs tw:text-sand-200">
          {runtime.authenticity.reasons.map((reason) => (
            <li key={reason}>{describeAuthenticityReason(reason, t)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AppShellFooter({ runtime }: { runtime: RuntimeState }) {
  const { t } = useTranslation();
  const provenance = runtime.authenticity.provenance;
  const writePathStatusFooterLabel = runtime.capabilities.writePathEnabled
    ? t("app.runtime.writePath.enabledLowercase")
    : t("app.runtime.writePath.failClosedLowercase");

  return (
    <footer className="tw:mx-auto tw:w-full tw:max-w-5xl tw:px-4 tw:py-8 tw:text-xs tw:text-iron-500">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
        <div>
          {t("app.runtime.footer.postureSummary")}
          {" · "}
          {t("app.runtime.footer.writePath", { state: writePathStatusFooterLabel })}
        </div>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <LocaleNeutralValue>v{provenance.appVersion}</LocaleNeutralValue>
          <a
            className="tw:text-iron-300 tw:no-underline tw:desktop-hover:text-iron-50"
            href={provenance.sourceRepoUrl}
            rel="noreferrer"
            target="_blank"
          >
            {t("app.runtime.footer.source")}
          </a>
        </div>
      </div>
    </footer>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const runtime = useRuntimeState();
  const primaryNavRoutes = APP_ROUTE_DEFINITIONS.filter(
    (route) => route.showInPrimaryNav !== false
  );
  const navRoutes =
    runtime.mode === "OUTSIDE_SAFE_READ_ONLY"
      ? primaryNavRoutes.filter((route) => route.outsideSafeReadOnlyAccess === "allowlisted")
      : primaryNavRoutes;

  return (
    <div className="tw:flex tw:min-h-screen tw:flex-col tw:bg-iron-950">
      <SkipToMainContentLink label={t("app.skipToMainContent")} />
      <AppShellHeader runtime={runtime} navRoutes={navRoutes} />
      <main
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className="tw:mx-auto tw:w-full tw:max-w-5xl tw:flex-1 tw:px-4 tw:py-6"
      >
        <WritePathAlert runtime={runtime} />
        <Outlet />
      </main>
      <AppShellFooter runtime={runtime} />
    </div>
  );
}
